import MiiaFwConfig from '@/configs/config'
import { openDB, type IDBPDatabase } from 'idb'

/**
 * indexedDBに保存するトークン管理オブジェクトのベース
 * 画像データと、どのような操作をされたかを持っている
 */
interface DeviceTokenState {
  /**
   * トークン
   */
  deviceToken: string
}
/**
 * トークンが格納されているindexedDBの管理を行うクラス
 * ここでは必要最低限の機能を提供する
 */
export class AuthDeviceDB {
  /**
   * indexedDBデータベース名
   */
  private static readonly _DB_NAME: string = 'DisasterEmergencyAuthDeviceDB'
  private static readonly _DB_STORE_NAME: string = 'AuthDeviceDB'
  /**
   * シングルトンインスタンス
   */
  private static _instance: AuthDeviceDB
  /**
   * indexedDBクラス
   */
  private static _db: Promise<IDBPDatabase>
  /**
   * シングルトンインスタンスを取得する
   * @readonly
   * @param なし
   * @returns PhotoDB - インスタンス
   */
  public static get instance(): AuthDeviceDB {
    if (!AuthDeviceDB._instance) {
      // もしobjectStoreの構造を変更した場合、src/define/config.tsのMiiaFwConfig.config.disasterInv.indexedDBVersion.deviceAuthの値を1増加させる
      const db = openDB(
        AuthDeviceDB._DB_NAME,
        MiiaFwConfig.config.disasterEmergency.indexedDBVersion.deviceAuth,
        {
          upgrade(db) {
            // createObjectStoreする際に同じobjectStoreがある場合エラーとなってしまうため、一度全部削除する
            if (db.objectStoreNames.contains(AuthDeviceDB._DB_STORE_NAME)) {
              db.deleteObjectStore(AuthDeviceDB._DB_STORE_NAME)
            }
            // 本来はこちらでStoreのAutoIncrementをなくして新しくStoreを作りたいが、作成しなおすと現行のiPadの認証がすべて途切れてしまうためそのままにしている
            const store = db.createObjectStore(AuthDeviceDB._DB_STORE_NAME, {
              keyPath: 'id',
              autoIncrement: true
            })
            store.createIndex('deviceToken', 'deviceToken', { unique: true })
          }
        }
      )
      AuthDeviceDB._instance = new AuthDeviceDB()
      AuthDeviceDB._db = db
      return AuthDeviceDB._instance
    }
    return AuthDeviceDB._instance
  }

  /**
   * indexedDBにDeviceTokenStateを挿入する
   * @param value 挿入したいDeviceTokenState
   * @returns AuthDeviceDBインスタンス
   */
  public async insertItem(value: DeviceTokenState): Promise<void> {
    const store = await AuthDeviceDB._db
    const allKeys = await store.getAllKeys(AuthDeviceDB._DB_STORE_NAME)
    // 過去のDeviceTokenが使えないように、新しくinsertする場合はすべてのdeviceTokenを削除する
    if (allKeys.length !== 0) {
      for (const key of allKeys) {
        await store.delete(AuthDeviceDB._DB_STORE_NAME, key)
      }
    }
    await store.put(AuthDeviceDB._DB_STORE_NAME, { deviceToken: value })
  }

  /**
   * deviceTokenがindexedDBに存在するかどうか
   * @param なし
   * @returns {boolean} 存在していればtrue,存在していないもしくはdeviceTokenがundefined、空文字であればfalseを返す
   */
  public async existsDeviceToken(): Promise<boolean> {
    const store = await AuthDeviceDB._db
    return await store
      .getAllKeys(AuthDeviceDB._DB_STORE_NAME)
      .then(result => {
        // データがDBに存在しない場合にはundefinedで返ってくるのと、
        // hash値としてnullや空文字は不適切なのでその場合はfalseとする
        return result[0] != null && result[0] !== ''
      }) // 存在した場合、trueを返す
      .catch(e => {
        console.error(e)
        return false
      }) // 存在しない場合、エラー発生するのでキャッチしてfalseを返す
  }

  /**
   * deviceTokenを取得する
   * @param なし
   * @returns {string} デバイストークン。存在しない場合は空文字を返す
   */
  public async getDeviceToken(): Promise<string | undefined> {
    const store = await AuthDeviceDB._db
    return await store
      .getAll(AuthDeviceDB._DB_STORE_NAME)
      .then(result => {
        return result[0].deviceToken.deviceToken as string
      })
      .catch(() => {
        return ''
      })
  }
}
